import { useLoaderData, useNavigate } from 'react-router';
import { useIntl } from 'react-intl';
import { useLocaleAutoSwitch } from 'i18n/localeUtils';
import Page from 'client/components/aria/Page/Page';
import MapPreview from 'client/components/MapPreview/MapPreview';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Markdown from 'client/components/aria/MarkDown/Markdown';
import SummaryItem from 'client/components/ui/SummaryItem/SummaryItem';
import { bufferedPoint } from 'client/utils/helpers/Aoi';
import styles from '../SaveObservationPage/SaveObservationPage.module.scss';
import Button from 'client/components/aria/Button/Button';
import { SAVE_OBSERVATION_PAGE } from 'client/utils/routes/routeConstants';

const ViewObservationPage = () => {
    const intl = useIntl();
    useLocaleAutoSwitch();
    const loaderData = useLoaderData() || {};
    const navigate = useNavigate();
    const { observation, disclaimerContent } = loaderData;

    const featuresWithDraftObservation = {
        observation: [new Feature({
            geometry: new Point(observation.geometry.coordinates)
        })]
    };

    const handleEdit = () => {
        navigate(SAVE_OBSERVATION_PAGE + '/' + observation.systemId);
    };

    return <Page title={intl.formatMessage({ id: 'viewObservationPage.page.title' })} data-testid="ViewObservationPage">
        <div className="govuk-grid-row">
            <div className="govuk-grid-column-full">
                <h1 className='govuk-heading-xl govuk-!-margin-bottom-4'>
                    {intl.formatMessage({ id: 'viewObservationPage.page.title' })}
                </h1>
            </div>
        </div>
        <div className="govuk-grid-row">
            <div className="govuk-grid-column-two-thirds">
                <SummaryItem title={intl.formatMessage({ id: 'viewObservationPage.displayTitle' })}>
                    {observation.objectName}
                </SummaryItem>
                <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible"/>
                <SummaryItem title={intl.formatMessage({ id: 'viewObservationPage.page.dateCollected' })}>
                    {observation?.dateDataCollected ? new Date(observation?.dateDataCollected).toLocaleDateString() : 'N/A' }
                </SummaryItem>
                <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible"/>
                <SummaryItem title={intl.formatMessage({ id: 'viewObservationPage.page.dateModified' })}>
                    {observation?.dateLastUpdated ? new Date(observation?.dateLastUpdated).toLocaleDateString() : 'N/A' }
                </SummaryItem>
                <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible"/>
                <SummaryItem title={intl.formatMessage({ id: 'viewObservationPage.observationCategory' })}>
                    {observation.observationCategory}
                </SummaryItem>
                <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible"/>
                <SummaryItem title={intl.formatMessage({ id: 'viewObservationPage.description' })}>
                    {observation.notes}
                </SummaryItem>
                <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible"/>
                <div className="govuk-grid-column-one-third">
                    <Button onPress={handleEdit} className={styles.button}>
                        { intl.formatMessage({ id: 'viewObservationPage.edit' })}
                    </Button>
                </div>
            </div>
            <div className="govuk-grid-column-one-third">
                <h2>
                    {intl.formatMessage({ id: 'viewObservationPage.mapPreview.title' })}
                </h2>
                <MapPreview
                    aoi={bufferedPoint(observation.geometry.coordinates, 25)}
                    featuresByAssetType={featuresWithDraftObservation}
                />
                <p className="govuk-body-s">
                    <span className="govuk-!-font-weight-bold">{intl.formatMessage({ id: 'viewObservationPage.coordinates' })}</span>
                    <span data-testid='viewObservationPage.coordinates'>{`${observation.geometry.coordinates[0].toFixed(2)}, ${observation.geometry.coordinates[1].toFixed(2)}`}</span>
                </p>
            </div>
        </div>
        <Markdown data-testid={'viewObservationPage.cmsContent'}>
            { disclaimerContent.markdown }
        </Markdown>
    </Page>;
};

export default ViewObservationPage;
